// src/components/panels/CreateDiscussionComposer.jsx
"use client";

import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HelpCircle, Search, X, ChevronDown, Image as ImageIcon, Smile } from "lucide-react";
import dynamic from "next/dynamic";
import { useDiscussionComposer } from "./useDiscussionComposer";
import ImagePreviewCarousel from "@/components/images/ImagePreviewCarousel";
import { showToast } from "@/components/ToastCenter";
import LinkPreviewCard from "@/components/content/LinkPreviewCard";
import { db, auth } from "@/firebase/config";
import { detectIsMobile } from "@/lib/device";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  limit,
  query,
  where,
  documentId,
} from "@/lib/firestore-instrumented";
import { useTypeaheadSearch } from "@/hooks/useTypeaheadSearch";
import { supportsNativeEmojiInput, triggerHapticFeedback } from "@/lib/emojiInput";
import { usePortal } from "@/context/PortalContext";

const ImageDropzone = dynamic(() => import("@/components/uploader/ImageDropzone"), {
  ssr: false,
  loading: () => (
    <div className="rounded-lg border-2 border-dashed border-[var(--border)] bg-[var(--surface-1)] px-3 py-6 text-center text-sm text-[var(--muted)]">
      Loading uploader…
    </div>
  ),
});

export default function CreateDiscussionComposer(props) {
  const portal = usePortal();
  const {
    mode,                 // "post" | "comment" | "reply"
    hubSlug,              // hub context (for comment/reply)
    threadId,             // required for comment/reply
    parentId,             // reply-only
    onSubmitted,          // called on success so host can close
    onCancel,             // optional cancel handler (panel hides composer)
    showHubSelect = false, // show hub picker when outside hub; inside hub we still show picker (members preselected)
    initialMention = "",   // optional handle (e.g., "@saul") to prefills on comment/reply
    variant = "default",
    placeholder,
    showMarkdownHelper = true,
    showDropzone = true,
    autoFocus = false,
    focusTrigger = 0,
  } = props;

  const isInline = variant === "inline";
  const showFooterCancel = !!onCancel && !isInline;
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [mentionTrigger, setMentionTrigger] = useState(null); // "@" or "+"
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIndex, setMentionIndex] = useState(0);

  // ---------------- Hub picker (combobox) ----------------
  const [hubs, setHubs] = useState([]);          // [{ slug, name, badgeUrl }]
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedHubSlug, setSelectedHubSlug] = useState("");
  const [userSelectedManually, setUserSelectedManually] = useState(false);

  const effectiveHub = useMemo(() => {
    return mode === "post" ? (selectedHubSlug || "") : (hubSlug || "");
  }, [mode, selectedHubSlug, hubSlug]);

  // Show picker for posts (both outside and inside hubs — inside we preselect only if member)
  const showPicker = mode === "post" && (showHubSelect || !!hubSlug);

  // Load allowed hubs: active memberships for current user
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!showPicker) return;
      const u = auth.currentUser;
      if (!u?.uid) { setHubs([]); return; }

      try {
        // membership rows for this user where status == "active"
        const memSnap = await getDocs(
          query(
            collection(db, "hubMembers"),
            where("uid", "==", u.uid),
            where("status", "==", "active"),
            limit(250)
          )
        );
        const hubIds = Array.from(
          new Set(memSnap.docs.map(d => String(d.data()?.hubId || "").trim()).filter(Boolean))
        ).filter((slug) => !portal || portal.allowedHubSlugSet?.has(slug));
        if (hubIds.length === 0) { setHubs([]); return; }

        // join to hubs (10 per chunk)
        const results = [];
        for (let i = 0; i < hubIds.length; i += 10) {
          const slice = hubIds.slice(i, i + 10);
          const snap = await getDocs(query(collection(db, "hubs"), where(documentId(), "in", slice)));
          snap.forEach(d => {
            const h = d.data() || {};
            results.push({
              slug: d.id,
              name: h.name || h.displayName || d.id,
              badgeUrl: h.badgeUrl || null,
            });
          });
        }
        results.sort((a, b) => a.slug.localeCompare(b.slug));
        if (mounted) setHubs(results);
      } catch (e) {
        console.warn("CreateDiscussionComposer: failed to load member hubs", e);
        if (mounted) setHubs([]);
      }
    })();
    return () => { mounted = false; };
  }, [showPicker, portal]);

  // Inside a hub: preselect it ONLY if user is a member; otherwise leave “Select a hub…”
  useEffect(() => {
    if (!showPicker || userSelectedManually) return;
    if (!hubSlug) { setSelectedHubSlug(""); return; }
    if (!hubs || hubs.length === 0) { setSelectedHubSlug(""); return; }
    const isMemberOfCurrent = hubs.some(h => h.slug === hubSlug);
    setSelectedHubSlug(isMemberOfCurrent ? hubSlug : "");
  }, [showPicker, hubSlug, hubs, userSelectedManually]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return hubs;
    return hubs.filter(h =>
      h.slug.toLowerCase().includes(q) || (h.name || "").toLowerCase().includes(q)
    );
  }, [hubs, filter]);

  // ---------------- Compose state & submit ----------------
  const {
    text, setText,
    files, addFiles, removeFile, clearFiles,
    submitting, error, canSubmit: baseCanSubmit,
    submit,
    linkPreview,
    setLinkPreview,
  } = useDiscussionComposer({
    mode,
    hubSlug: effectiveHub, // POST: from picker; COMMENT/REPLY: from prop
    threadId,
    parentId,
    onSubmitted,
  });

  useEffect(() => {
    if (!initialMention || mode === "post") return;
    setText((prev) => {
      const trimmedPrev = prev.trim();
      if (trimmedPrev.length > 0) return prev;
      const mention = initialMention.startsWith("@") ? initialMention : `@${initialMention}`; // guard double @
      return `${mention.trim()} `;
    });
  }, [initialMention, mode, setText]);

  const formRef = useRef(null);
  const textareaRef = useRef(null);
  const inlineFileInputRef = useRef(null);
  const emojiButtonWrapperRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const emojiInputRef = useRef(null); // hidden input for native emoji keyboard
  const keepComposerVisible = useCallback(() => {
    if (typeof window === "undefined") return;
    const target = formRef.current || textareaRef.current;
    if (!target) return;
    [80, 260].forEach((delay) => {
      window.setTimeout(() => {
        target.scrollIntoView({ block: "end", inline: "nearest" });
      }, delay);
    });
  }, []);

  useEffect(() => {
    if (!autoFocus) return;
    const node = textareaRef.current;
    if (!node) return;
    node.focus();
    const len = node.value.length;
    try {
      node.setSelectionRange(len, len);
    } catch {}
    keepComposerVisible();
  }, [autoFocus, focusTrigger, keepComposerVisible]);

  useEffect(() => {
    if (!autoFocus || typeof window === "undefined") return undefined;
    const viewport = window.visualViewport;
    if (!viewport) return undefined;
    const handleViewportResize = () => keepComposerVisible();
    viewport.addEventListener("resize", handleViewportResize);
    return () => viewport.removeEventListener("resize", handleViewportResize);
  }, [autoFocus, keepComposerVisible]);

  const [previewStatus, setPreviewStatus] = useState("idle"); // idle | loading | ready | error
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewError, setPreviewError] = useState("");
  const previewTimerRef = useRef(null);
  const lastFetchedRef = useRef("");
  const isMobile = useMemo(() => detectIsMobile(), []);
  const typeahead = useTypeaheadSearch({
    query: mentionQuery,
    types: {
      users: mentionTrigger === "@",
      agents: mentionTrigger === "@",
      hubs: mentionTrigger === "+",
    },
    limit: 5,
    debounceMs: 200,
  });
  const suggestionSections = useMemo(() => {
    if (!mentionTrigger) return [];
    const sections = [];
    if (mentionTrigger === "@") {
      const users = (typeahead.results.users || []).slice(0, 5).map((u) => ({
        key: `user-${u.id}`,
        label: u.displayName || `@${u.handle}`,
        handle: u.handle,
        avatar: u.avatar || "",
        type: "user",
      }));
      const agents = (typeahead.results.agents || []).slice(0, 5).map((a) => ({
        key: `agent-${a.id}`,
        label: a.displayName || `@${a.handle}`,
        handle: a.handle,
        avatar: a.avatar || "",
        type: "agent",
      }));
      if (users.length) sections.push({ title: "People", items: users });
      if (agents.length) sections.push({ title: "Agents", items: agents });
    } else if (mentionTrigger === "+") {
      const hubs = (typeahead.results.hubs || []).slice(0, 8).map((h) => ({
        key: `hub-${h.id}`,
        label: h.displayName || h.slug,
        handle: h.slug,
        avatar: h.badgeUrl || "",
        type: "hub",
      }));
      if (hubs.length) sections.push({ title: "Spaces", items: hubs });
    }
    return sections;
  }, [mentionTrigger, typeahead.results.users, typeahead.results.agents, typeahead.results.hubs]);

  const suggestionsFlat = useMemo(
    () => suggestionSections.flatMap((section) => section.items),
    [suggestionSections]
  );

  useEffect(() => {
    if (mentionIndex >= suggestionsFlat.length) {
      setMentionIndex(0);
    }
  }, [suggestionsFlat.length, mentionIndex]);

  const insertEmojiAtCursor = (emojiChar) => {
    setText((prev) => {
      const base = typeof prev === "string" ? prev : "";
      const textarea = textareaRef.current;
      if (!textarea) return `${base}${emojiChar}`;
      const start = textarea.selectionStart ?? base.length;
      const end = textarea.selectionEnd ?? base.length;
      const next = base.slice(0, start) + emojiChar + base.slice(end);
      requestAnimationFrame(() => {
        const caret = start + emojiChar.length;
        textarea.focus();
        textarea.setSelectionRange(caret, caret);
      });
      return next;
    });
  };

  const handleEmojiButtonClick = () => {
    textareaRef.current?.focus();
    if (supportsNativeEmojiInput()) {
      // Trigger native emoji keyboard
      if (emojiInputRef.current) {
        emojiInputRef.current.focus();
      }
      setShowEmojiPicker(false);
      return;
    }
    // Fallback: embedded picker
    setShowEmojiPicker((prev) => !prev);
  };

  const handleEmojiSelect = (emojiChar) => {
    insertEmojiAtCursor(emojiChar);
    triggerHapticFeedback(30);
    setShowEmojiPicker(false);
  };

  const handleNativeEmojiInput = (e) => {
    const val = e.target.value || "";
    if (!val) return;
    insertEmojiAtCursor(val);
    triggerHapticFeedback(30);
    e.target.value = "";
    try { e.target.blur(); } catch {}
  };

  const handleTextChange = (e) => {
    const value = e.target.value;
    const caret = e.target.selectionStart ?? value.length;
    setText(value);
    updateMentionState(value, caret);
  };

  const handleTextareaKeyDown = (e) => {
    if (!mentionTrigger || suggestionsFlat.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setMentionIndex((idx) => (idx + 1) % suggestionsFlat.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setMentionIndex((idx) => (idx - 1 + suggestionsFlat.length) % suggestionsFlat.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      applySuggestion(suggestionsFlat[mentionIndex] || suggestionsFlat[0]);
    } else if (e.key === "Tab") {
      e.preventDefault();
      applySuggestion(suggestionsFlat[mentionIndex] || suggestionsFlat[0]);
    } else if (e.key === "Escape") {
      resetMention();
    }
  };

  const resetMention = () => {
    setMentionTrigger(null);
    setMentionQuery("");
    setMentionIndex(0);
  };

  const updateMentionState = (value, caret) => {
    const before = value.slice(0, caret);
    const match = /(?:^|[\s])([@+])([a-zA-Z0-9-]{1,32})$/i.exec(before);
    if (match) {
      setMentionTrigger(match[1]);
      setMentionQuery(match[2].toLowerCase());
      setMentionIndex(0);
    } else {
      resetMention();
    }
  };

  const applySuggestion = (item) => {
    if (!item) return;
    const triggerChar = item.type === "hub" ? "+" : "@";
    const node = textareaRef.current;
    const caret = node?.selectionStart ?? text.length;
    const end = node?.selectionEnd ?? caret;
    const beforeFull = text.slice(0, caret);
    const after = text.slice(end);
    const match = /(?:^|[\s])([@+])([a-zA-Z0-9-]{0,32})$/i.exec(beforeFull);
    const start = match ? caret - (match[2]?.length || 0) - 1 : caret; // drop the trigger before inserting
    const prefix = text.slice(0, start);
    const insertion = `${triggerChar}${item.handle}`;
    const next = `${prefix}${insertion} ${after}`;
    setText(next);
    resetMention();
    requestAnimationFrame(() => {
      const nodeRef = textareaRef.current;
      if (!nodeRef) return;
      const pos = prefix.length + insertion.length + 1;
      nodeRef.focus();
      try {
        nodeRef.setSelectionRange(pos, pos);
      } catch {}
    });
  };

  useEffect(() => {
    if (!showEmojiPicker) return;
    const handlePointer = (event) => {
      const target = event.target;
      if (
        emojiPickerRef.current?.contains(target) ||
        emojiButtonWrapperRef.current?.contains(target)
      ) {
        return;
      }
      setShowEmojiPicker(false);
    };
    document.addEventListener("pointerdown", handlePointer);
    return () => document.removeEventListener("pointerdown", handlePointer);
  }, [showEmojiPicker]);

  useEffect(() => {
    const endpoint = process.env.NEXT_PUBLIC_GET_LINK_PREVIEW_URL;
    const firstMatch = text.match(/https?:\/\/[^\s<>()]+/i);
    const url = firstMatch ? firstMatch[0] : "";

    // Clear existing preview if no URL or files take precedence
    if (!url || files.length > 0 || !endpoint) {
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
        previewTimerRef.current = null;
      }
      setPreviewStatus("idle");
      setPreviewUrl("");
      setPreviewError("");
      if (linkPreview) setLinkPreview(null);
      return;
    }

    if (url === previewUrl && linkPreview) {
      return; // already have preview for this url
    }

    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);

    previewTimerRef.current = setTimeout(async () => {
      previewTimerRef.current = null;
      if (lastFetchedRef.current === url) return;
      try {
        setPreviewStatus("loading");
        setPreviewError("");
        setPreviewUrl(url);
        const resp = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url }),
        });
        if (!resp.ok) {
          throw new Error(`Failed (${resp.status})`);
        }
        const data = await resp.json();
        lastFetchedRef.current = url;
        setLinkPreview(data && data.url ? data : null);
        setPreviewError("");
        setPreviewStatus("ready");
      } catch (err) {
        console.warn("link preview fetch failed", err);
        setLinkPreview(null);
        setPreviewStatus("error");
        setPreviewError("Could not load preview");
      }
    }, 500);

    return () => {
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
        previewTimerRef.current = null;
      }
    };
  }, [text, files, linkPreview, setLinkPreview, previewUrl]);

  // For posts, require hub selection; for comment/reply require threadId & hub context (hook checks this too)
  const canSubmit = useMemo(() => {
    if (!baseCanSubmit || submitting) return false;
    if (mode === "post") return !!effectiveHub;
    return true;
  }, [baseCanSubmit, submitting, mode, effectiveHub]);

  const finalPlaceholder = placeholder || (mode === "post" ? "Add a post…" : mode === "comment" ? "Add a comment…" : "Add a reply…");
  const displayMarkdown = showMarkdownHelper && !isInline;
  const displayDropzone = showDropzone && !isInline;

  const handleInlineFileChange = (event) => {
    const filesList = Array.from(event.target.files || []);
    if (filesList.length) addFiles(filesList);
    if (event.target) event.target.value = "";
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;

    const isPost = mode === "post";
    const tempId = `tmp-${Date.now()}`;
    const targetHub = effectiveHub;

    // Optimistic shimmer for posts
    if (isPost && targetHub) {
      window.dispatchEvent(new CustomEvent("post:pending", { detail: { tempId, hubSlug: targetHub } }));
    }

    try {
      const result = await submit();

      // Success UX
      if (isPost) {
        const finalHub = result?.hubSlug || targetHub;
        if (!hubSlug) {
          showToast({
            intent: "success",
            message: `Posted in ${toTitle(finalHub || "space")}`,
            action: () => {
              const url = `/h/${encodeURIComponent(finalHub || "")}${
                result?.threadId ? `#thread-${result.threadId}` : ""
              }`;
              window.location.assign(url);
            },
          });
        } else {
          showToast({ intent: "success", message: "Your post has been published" });
          if (result?.threadId) {
            window.dispatchEvent(new CustomEvent("post:await", { detail: { threadId: result.threadId } }));
          }
        }
        if (targetHub) {
          window.dispatchEvent(new CustomEvent("post:resolve", { detail: { tempId, hubSlug: targetHub } }));
        }
      } else {
        // comment / reply success
        showToast({
          intent: "success",
          message: mode === "comment" ? "Your comment has been published." : "Your reply has been published.",
        });
      }

      // Let host close (drawer/panel) on success
      onSubmitted?.(result);
    } catch (err) {
      console.error("CreateDiscussionComposer submit failed:", err);
      let msg = "Couldn’t publish. Please try again.";
      const codeText = `${err?.code || ""}`.toLowerCase();
      if (!navigator.onLine) msg = "No internet connection.";
      else if (codeText.includes("failed-precondition") || codeText.includes("permission")) {
        msg = "You don’t have permission to post in this space.";
      }
      showToast({ intent: "error", message: msg });

      // Resolve pending shimmer if a post failed
      if (mode === "post" && targetHub) {
        window.dispatchEvent(new CustomEvent("post:resolve", { detail: { tempId, hubSlug: targetHub } }));
      }
    }
  };

  const submitButton = (
    <button
      type="submit"
      disabled={!canSubmit}
      className="btn inline-flex shrink-0 items-center gap-2"
    >
      {submitting && <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />}
      {submitting ? `${labelFor(mode)}ing…` : labelFor(mode)}
    </button>
  );

  const inlineActionRow = isInline ? (
    <div className="flex items-center justify-between gap-3 text-xs text-[var(--muted)]">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <input
          ref={inlineFileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleInlineFileChange}
        />
        <button
          type="button"
          onClick={() => inlineFileInputRef.current?.click()}
          className="inline-flex items-center gap-1 rounded-full px-3 py-1 font-medium hover:text-[var(--brand)]"
        >
          <ImageIcon className="h-4 w-4" /> Images
        </button>
        <div className="relative" ref={emojiButtonWrapperRef}>
          {/* Hidden input to trigger native emoji keyboard on mobile */}
          <input
            ref={emojiInputRef}
            type="text"
            inputMode="text"
            onChange={handleNativeEmojiInput}
            className="absolute left-0 top-0 h-px w-px opacity-0 pointer-events-none"
            aria-hidden="true"
            tabIndex={-1}
          />
          <button
            type="button"
            onClick={handleEmojiButtonClick}
            className="inline-flex items-center gap-1 rounded-full px-3 py-1 font-medium hover:text-[var(--brand)]"
            aria-haspopup="dialog"
            aria-expanded={showEmojiPicker}
          >
            <Smile className="h-4 w-4" /> Emoji
          </button>
          {showEmojiPicker ? (
            <EmojiPickerPopover ref={emojiPickerRef} onSelect={handleEmojiSelect} />
          ) : null}
        </div>
        {files.length > 0 && (
          <button
            type="button"
            onClick={clearFiles}
            className="inline-flex items-center gap-1 rounded-full px-3 py-1 font-medium hover:text-[var(--fg-secondary)]"
          >
            <X className="h-3.5 w-3.5" /> Clear images
          </button>
        )}
      </div>
      {submitButton}
    </div>
  ) : null;

  return (
    <form ref={formRef} className="space-y-4" onSubmit={handleSubmit}>
      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Hub picker (posts only; inside hub still shown for consistency, preselect if member) */}
      {showPicker && (
        <div className="relative z-30">
          <label htmlFor="hub-combobox" className="text-xs text-[var(--fg-secondary)] block mb-1">
            Post to
          </label>

          <button
            type="button"
            id="hub-combobox"
            aria-haspopup="listbox"
            aria-expanded={open}
            onClick={() => setOpen(v => !v)}
            className="select w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-2 truncate">
              <HubBadgeImg item={hubs.find(h => h.slug === (effectiveHub || ""))} />
              <span className="truncate">
                {effectiveHub
                  ? `${effectiveHub} — ${hubs.find(h => h.slug === effectiveHub)?.name || toTitle(effectiveHub)}`
                  : "Select a space…"}
              </span>
            </div>
            <ChevronDown className="h-4 w-4 opacity-70" />
          </button>

          {open && (
            <div
              role="listbox"
              tabIndex={-1}
              className="absolute z-70 mt-1 w-full rounded-[var(--radius)] border border-[var(--border)] bg-[var(--card)] shadow-[var(--shadow-md)]"
            >
              <div className="flex items-center gap-2 px-2 py-2 border-b border-[var(--border)]">
                <Search className="h-4 w-4 text-[var(--muted)]" />
                <input
                  autoFocus
                  type="text"
                  placeholder="Filter spaces…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="flex-1 bg-transparent text-sm outline-none"
                />
              </div>

              <ul className="max-h-64 overflow-y-auto py-1">
                {filtered.length === 0 ? (
                  <li className="px-3 py-2 text-sm text-[var(--muted)]">No matches</li>
                ) : (
                  filtered.map((h) => (
                    <li
                      key={h.slug}
                      role="option"
                      aria-selected={h.slug === effectiveHub}
                      onClick={() => {
                        setSelectedHubSlug(h.slug);
                        setUserSelectedManually(true);
                        setOpen(false);
                      }}
                      className={[
                        "flex cursor-pointer items-center gap-2 px-3 py-2 text-sm",
                        h.slug === effectiveHub ? "bg-[var(--surface-2)]" : "hover:bg-[var(--surface-2)]",
                      ].join(" ")}
                    >
                      <HubBadgeImg item={h} />
                      <div className="min-w-0">
                        <div className="truncate font-medium">+{h.slug}</div>
                        <div className="truncate text-xs text-[var(--muted)]">{h.name}</div>
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Textarea */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleTextChange}
          onKeyDown={handleTextareaKeyDown}
          onFocus={keepComposerVisible}
          rows={5}
          placeholder={finalPlaceholder}
          className="textarea w-full"
        />
        {mentionTrigger && (typeahead.loading || suggestionsFlat.length > 0) ? (
          <div
            className="absolute left-0 right-0 z-50 mt-1 max-h-64 overflow-auto rounded-[var(--radius)] border border-[var(--border)] bg-[var(--card)] shadow-[var(--shadow-md)]"
            role="listbox"
          >
            {typeahead.loading ? (
              <div className="px-3 py-2 text-sm text-[var(--muted)]">Searching…</div>
            ) : null}
            {suggestionSections.map((section) => (
              <div key={section.title}>
                <div className="px-3 pt-2 text-[11px] uppercase tracking-wide text-[var(--muted)]">
                  {section.title}
                </div>
                {section.items.map((item, idx) => {
                  const flatIndex = suggestionsFlat.findIndex((it) => it.key === item.key);
                  const active = flatIndex === mentionIndex;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      role="option"
                      aria-selected={active}
                      className={[
                        "flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[var(--surface-2)]",
                        active ? "bg-[var(--surface-2)]" : "",
                      ].join(" ")}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => applySuggestion(item)}
                    >
                      {item.avatar ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={item.avatar} alt="" className="h-7 w-7 rounded-full object-cover" />
                      ) : (
                        <div className="h-7 w-7 rounded-full bg-[var(--muted)]/20" aria-hidden />
                      )}
                      <div className="flex-1 truncate">
                        <div className="truncate font-medium">
                          {item.type === "hub" ? `+${item.handle}` : `@${item.handle}`}
                        </div>
                        <div className="truncate text-xs text-[var(--muted)]">
                          {item.label}
                          {item.type === "agent" ? " • Agent" : null}
                          {item.type === "hub" ? " • Space" : null}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}
            {!typeahead.loading && suggestionsFlat.length === 0 ? (
              <div className="px-3 py-2 text-sm text-[var(--muted)]">No matches</div>
            ) : null}
          </div>
        ) : null}
      </div>

      {inlineActionRow}

      {previewStatus === "loading" ? (
        <div className="rounded-[var(--radius)] border border-dashed border-[var(--border)] bg-[var(--surface-1)] p-3 text-sm text-[var(--muted)]">
          Fetching preview…
        </div>
      ) : null}

      {previewStatus === "error" && previewError ? (
        <div className="rounded-[var(--radius)] border border-dashed border-red-400/40 bg-red-50/40 p-3 text-xs text-red-600">
          {previewError}
        </div>
      ) : null}

      {previewStatus === "ready" && linkPreview ? (
        <LinkPreviewCard preview={linkPreview} />
      ) : null}

      {displayMarkdown ? (
        <a
          href="https://www.markdownguide.org/cheat-sheet/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-[var(--muted)] hover:text-[var(--fg-secondary)]"
          title="Supports markdown. Go to markdownguide.org/cheat-sheet to learn more."
        >
          <HelpCircle className="h-4 w-4" />
          Markdown
        </a>
      ) : null}

      {displayDropzone ? <ImageDropzone onFiles={addFiles} /> : null}
      {files.length > 0 && (
        <ImagePreviewCarousel files={files} onRemove={(idx) => removeFile(idx)} />
      )}

      {!isInline ? (
        <div className="flex items-center gap-3 pt-1">
          {files.length > 0 && (
            <button
              type="button"
              onClick={clearFiles}
              className="inline-flex items-center gap-1 text-xs text-[var(--muted)] hover:text-[var(--fg-secondary)]"
            >
              <X className="h-3.5 w-3.5" /> Clear images
            </button>
          )}

          {showFooterCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="text-sm text-[var(--muted)] hover:text-[var(--fg-secondary)]"
            >
              Cancel
            </button>
          )}

          <div className="ml-auto">{submitButton}</div>
        </div>
      ) : null}
    </form>
  );
}

/* bits */
function labelFor(mode) {
  return mode === "post" ? "Post" : mode === "comment" ? "Comment" : "Reply";
}
function HubBadgeImg({ item }) {
  const src = item?.badgeUrl || "";
  if (!src) {
    return <div className="h-6 w-6 rounded-md bg-[var(--muted)]/20 border border-[var(--border)]" aria-hidden />;
  }
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={src} alt="" className="h-6 w-6 rounded-md object-cover border border-[var(--border)]" />;
}
function toTitle(slug = "") {
  return String(slug).split("-").map((s) => (s ? s[0].toUpperCase() + s.slice(1) : "")).join(" ");
}

const QUICK_EMOJIS = [
  "😀",
  "😁",
  "😂",
  "🤣",
  "😊",
  "😍",
  "😘",
  "😎",
  "🤩",
  "😇",
  "🤔",
  "🤗",
  "🤝",
  "👍",
  "🙏",
  "🔥",
  "✨",
  "🎉",
  "🥳",
  "💡",
  "💬",
  "❤️",
  "💙",
  "💚",
  "💛",
  "💜",
  "🧠",
  "🤖",
  "🌟",
  "🍀",
  "☕",
  "📎",
];

const EmojiPickerPopover = forwardRef(function EmojiPickerPopover({ onSelect }, ref) {
  return (
    <div
      ref={ref}
      className="absolute bottom-full left-1/2 z-50 mb-3 w-56 -translate-x-1/2 rounded-lg border border-[var(--border)] bg-[var(--card)] p-2 shadow-xl"
    >
      <div className="mb-2 text-xs font-medium text-[var(--muted)]">Quick emoji</div>
      <div className="grid grid-cols-8 gap-1">
        {QUICK_EMOJIS.map((emoji) => (
          <button
            type="button"
            key={emoji}
            className="rounded-md p-1 text-lg hover:bg-[var(--surface-2)]"
            onClick={() => onSelect?.(emoji)}
          >
            {emoji}
          </button>
        ))}
      </div>
      <div className="mt-2 text-[10px] text-[var(--muted)]">
        Tip: use <kbd className="rounded border px-1">Win + .</kbd> or <kbd className="rounded border px-1">⌃⌘ Space</kbd> for the full
        desktop picker.
      </div>
    </div>
  );
});
